/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Typography, Modal, List, message } from 'antd';
import { useContext, useState } from 'react';
import { useIntl } from 'react-intl';

// local imports
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import IconButton from '~components/_common/IconButton/IconButton';
import LargeFilesContext from '~src/contexts/LargeFilesContext';
import { formatBytes } from '~src/utils/formatBytes';
import { downloadSelectedVersion } from '~src/utils/download';
import { SimpleObject } from '~src/types';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';

export const LargeFilesModal: React.FC = () => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const { largeFilesContext, setLargeFilesContext } = useContext(
    LargeFilesContext
  );
  const [visible, setVisible] = useState(true);
  const [triedItemIds, setTriedItemIds] = useState<SimpleObject<boolean>>({});

  return (
    <Modal
      title={
        <ModalTitle
          text={intl.formatMessage({ id: 'large-files' })}
          icon={['far', 'file-download']}
          iconButtonProps={{ color: 'yellow' }}
        />
      }
      centered
      width={450}
      footer={null}
      maskClosable={false}
      visible={visible}
      onCancel={() => {
        setVisible(false);
        setTimeout(() => setLargeFilesContext([]), 400);
      }}>
      <Typography.Text>
        {intl.formatMessage({ id: 'large-files-helper' })}
      </Typography.Text>

      <List
        bordered
        css={styles.list}
        dataSource={largeFilesContext}
        renderItem={(item) => (
          <List.Item
            key={item.id}
            css={styles.listItem}
            extra={[
              <div key={item.id + '-actions'}>
                <IconButton
                  icon={['far', 'download']}
                  containerStyle={{ marginRight: -10 }}
                  color={triedItemIds[item.id] ? 'green' : undefined}
                  tooltip={intl.formatMessage({ id: 'download' })}
                  click={() => {
                    message.info(
                      intl.formatMessage({ id: 'download-starting' })
                    );
                    downloadSelectedVersion(item);
                    setTriedItemIds((prevTriedItems) => ({
                      ...prevTriedItems,
                      [item.id]: true,
                    }));
                  }}
                />
              </div>,
            ]}>
            <Typography.Text strong>
              {item.title} ({formatBytes(item.size || 0, 1)})
            </Typography.Text>
          </List.Item>
        )}
      />
    </Modal>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  list: {
    maxHeight: 307,
    overflowY: 'auto',
    margin: '14px 0',
  },
  listItem: {
    ':hover': {
      background: 'rgb(250, 250, 250)',
    },
  },
});
